import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuthStore } from "../../stores/root_store";
import { taskPaymentApi } from "../../api/task_payment_api";
import type {
  PaymentBatchDetail,
  VoidBatchResult,
} from "../../api/task_payment_api";
import {
  getPaymentBatchStatusColor,
  isPaymentBatchVoidable,
} from "./paymentBatchStatus";

// Older batches are more likely to have had their CSV sent already
const VOID_AGE_WARNING_DAYS = 3;

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString();
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString();
}

function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function getBatchTypeLabel(type: string): string {
  switch (type) {
    case "run":
      return "Regular Payment";
    case "missing":
      return "Missing Fix";
    case "mentor-only":
      return "Mentor Only";
    case "repair-pay-via-shift":
      return "Shift-Flag Fix";
    default:
      return type;
  }
}

interface StatCardProps {
  label: string;
  value: string | number;
  secondary?: string;
}

function StatCard({ label, value, secondary }: StatCardProps) {
  return (
    <Paper sx={{ p: 2, textAlign: "center" }} variant="outlined">
      <Typography variant="h5" component="div">
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {secondary && (
        <Typography variant="caption" color="text.secondary">
          {secondary}
        </Typography>
      )}
    </Paper>
  );
}

export const PaymentBatchDetailPage = observer(() => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const authStore = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [batch, setBatch] = useState<PaymentBatchDetail | null>(null);
  const [voidPreview, setVoidPreview] = useState<VoidBatchResult | null>(null);
  const [voidLoading, setVoidLoading] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voidConfirmed, setVoidConfirmed] = useState(false);

  const hasSudo = authStore.roles.includes("sudo");

  const loadBatch = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await taskPaymentApi.getBatch(id);
      setBatch(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load batch");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasSudo && runId) {
      loadBatch(runId);
    }
  }, [hasSudo, runId]);

  const handlePreviewVoid = async () => {
    if (!runId) return;
    try {
      setVoidLoading(true);
      setVoidError(null);
      setVoidConfirmed(false);
      setVoidPreview(await taskPaymentApi.voidBatch(runId, true));
    } catch (err) {
      setVoidError(err instanceof Error ? err.message : "Failed to preview void");
    } finally {
      setVoidLoading(false);
    }
  };

  const closeVoidDialog = () => {
    setVoidPreview(null);
    setVoidError(null);
  };

  const handleExecuteVoid = async () => {
    if (!runId) return;
    try {
      setVoidLoading(true);
      setVoidError(null);
      await taskPaymentApi.voidBatch(runId, false);
      setVoidPreview(null);
      await loadBatch(runId);
    } catch (err) {
      setVoidError(err instanceof Error ? err.message : "Failed to void batch");
    } finally {
      setVoidLoading(false);
    }
  };

  if (!authStore.initialized) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!hasSudo) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          You must have sudo access to view this page.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/assistant")}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/manager/task-payment/history")}
          sx={{ mb: 2 }}
        >
          Back to History
        </Button>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !batch) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/manager/task-payment/history")}
          sx={{ mb: 2 }}
        >
          Back to History
        </Button>
        <Alert severity="error">{error || "Batch not found"}</Alert>
      </Container>
    );
  }

  const batchAgeDays = Math.floor(
    (Date.now() -
      new Date(batch.date_completed ?? batch.date_created).getTime()) /
      (24 * 60 * 60 * 1000)
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/manager/task-payment/history")}
        sx={{ mb: 2 }}
      >
        Back to History
      </Button>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h4">Payment Batch Details</Typography>
        <Chip
          label={getBatchTypeLabel(batch.batch_type)}
          color="primary"
          size="small"
        />
        <Chip
          label={batch.status}
          color={getPaymentBatchStatusColor(batch.status)}
          size="small"
        />
        {batch.has_errors && (
          <Chip label="has errors" color="error" size="small" />
        )}
        {isPaymentBatchVoidable(batch.status) && (
          <Button
            variant="outlined"
            color="error"
            onClick={handlePreviewVoid}
            disabled={voidLoading}
            sx={{ ml: "auto" }}
          >
            Void Batch
          </Button>
        )}
      </Box>

      {batch.status === "voided" && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Voided {formatDateTime(batch.date_voided)}
          {batch.voided_by_email && ` by ${batch.voided_by_email}`}. The tasks
          and mentor claims it paid were marked unpaid and will be included in
          the next payment run.
        </Alert>
      )}

      {voidError && !voidPreview && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {voidError}
        </Alert>
      )}

      <Dialog
        open={voidPreview !== null}
        onClose={() => !voidLoading && closeVoidDialog()}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Void this payment batch?</DialogTitle>
        <DialogContent>
          {voidPreview && (
            <>
              {batchAgeDays >= VOID_AGE_WARNING_DAYS && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This batch ran {batchAgeDays} days ago. Make sure its CSV was
                  never sent to Dwolla before voiding it.
                </Alert>
              )}
              {voidPreview.unrecorded_user_keys.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {voidPreview.unrecorded_user_keys.length} user(s) have no
                  payment record from this run (see Errors below). Voiding
                  won&apos;t change their tasks.
                </Alert>
              )}
              <Typography variant="body2" sx={{ mb: 2 }}>
                This marks {voidPreview.request_count} task(s) and{" "}
                {voidPreview.mentor_claim_count} mentor claim(s) totaling{" "}
                {formatCurrency(voidPreview.reverted_amount)} unpaid so they are
                paid in the next run, and deletes {voidPreview.payment_count}{" "}
                payment record(s) totaling{" "}
                {formatCurrency(voidPreview.total_amount)}.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Tasks</TableCell>
                    <TableCell align="right">Mentor Claims</TableCell>
                    <TableCell align="right">Marked Unpaid</TableCell>
                    <TableCell align="right">Payment Record</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voidPreview.summary.map((row) => (
                    <TableRow key={row.user_key ?? row.email}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell align="right">{row.request_count}</TableCell>
                      <TableCell align="right">
                        {row.mentor_claim_count}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.reverted_amount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Checkbox
                    checked={voidConfirmed}
                    onChange={(e) => setVoidConfirmed(e.target.checked)}
                  />
                }
                label="The CSV for this batch was NOT sent to Dwolla"
              />
              {voidError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {voidError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVoidDialog} disabled={voidLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleExecuteVoid}
            disabled={!voidConfirmed || voidLoading}
          >
            {voidLoading ? <CircularProgress size={20} /> : "Void Batch"}
          </Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Batch Information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Run ID
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                <code>{batch.run_id}</code>
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Date Executed
              </Typography>
              <Typography variant="body1">
                {formatDateTime(batch.date_created)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Date Range
              </Typography>
              <Typography variant="body1">
                {formatDate(batch.date_range_start)} -{" "}
                {formatDate(batch.date_range_end)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Operator
              </Typography>
              <Typography variant="body1">{batch.operator_email}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Total Amount"
                value={formatCurrency(batch.total_amount)}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard label="Users Paid" value={batch.user_count} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label={
                  batch.batch_type === "mentor-only"
                    ? "Mentor Claims"
                    : "Requests"
                }
                value={
                  batch.batch_type === "mentor-only"
                    ? batch.mentor_claim_count
                    : batch.request_count
                }
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Avg per User"
                value={formatCurrency(batch.avg_per_user)}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Max Payment"
                value={formatCurrency(batch.max_payment)}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Min Payment"
                value={formatCurrency(batch.min_payment)}
              />
            </Grid>
          </Grid>

          {batch.skipped_user_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {batch.skipped_user_count} user(s) were skipped (e.g., no Dwolla
              account)
            </Alert>
          )}
        </CardContent>
      </Card>

      {batch.errors && batch.errors.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="error" gutterBottom>
              Errors ({batch.errors.length})
            </Typography>
            {batch.errors.map((err, i) => (
              <Alert severity="error" key={i} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Phase:</strong> {err.phase}
                  {err.user_key && (
                    <>
                      {" | "}
                      <strong>User:</strong> {err.user_key}
                    </>
                  )}
                  {err.batch_start !== undefined && (
                    <>
                      {" | "}
                      <strong>Batch:</strong> {err.batch_start}-
                      {(err.batch_start ?? 0) + (err.batch_size ?? 0)}
                    </>
                  )}
                </Typography>
                <Typography variant="body2">{err.error}</Typography>
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Records ({batch.payment_count})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {batch.payment_keys.length === 0 ? (
            <Alert severity="info">No individual payment records linked.</Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {batch.payment_count} individual FHAdminPayment record(s) were
              created as part of this batch. These are linked by run_id:{" "}
              <code>{batch.run_id}</code>
            </Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  );
});
